/**
 * Set entity schema and mapping helpers for use with GenericEntityUpsertDialog.
 * Form/store keys use API (PascalCase) so PATCH paths match; create payload may use camelCase per API.
 */

import { createEntityRef, entityHasEntityRef, getEntityValueFromARef } from '../types';
import { getAttributeName } from './schemaAttributeUtils';

export interface SetFormAttribute {
    name: string;
    id: string;
    type: string;
    multiValued?: boolean;
    required?: boolean;
    label: string;
}

/** Synthetic schema for Set so the generic dialog can drive form and PATCH. */
export const SET_SCHEMA_ATTRIBUTES: SetFormAttribute[] = [
    { name: 'DisplayName', id: 'DisplayName', type: 'string', required: true, label: 'Display Name' },
    { name: 'Description', id: 'Description', type: 'string', label: 'Description' },
    { name: 'FilterScope', id: 'FilterScope', type: 'string', required: true, label: 'Filter Scope (Entity Type)' },
    { name: 'Type', id: 'Type', type: 'string', label: 'Type' },
    { name: 'IsSystem', id: 'IsSystem', type: 'boolean', label: 'Is System' },
    { name: 'ManualSetMember', id: 'ManualSetMember', type: 'string', multiValued: true, label: 'Manual Members' },
    { name: 'Filter', id: 'Filter', type: 'string', label: 'Filter' }
];

/**
 * Map API/entity shape to form data (PascalCase keys for formData in generic dialog).
 */
export function entityToSetFormData(entity: any): Record<string, any> {
    if (!entity || typeof entity !== 'object') {
        return {
            DisplayName: '',
            Description: '',
            FilterScope: '',
            Type: 'Manual',
            IsSystem: false,
            ManualSetMember: [],
            Filter: ''
        };
    }
    const filterScope = entity.FilterScope ?? entity.filterScope;
    const filterScopeStr = typeof filterScope === 'string'
        ? filterScope
        : (filterScope && typeof filterScope === 'object')
            ? (filterScope.Id ?? filterScope.DisplayName ?? filterScope.id ?? filterScope.displayName ?? '')
            : '';
    const manualMember = entity.ManualSetMember ?? entity.manualSetMember ?? [];
    const manualArr = Array.isArray(manualMember)
        ? manualMember.map((m: any) => {
            if (m == null) return null;
            if (typeof m === 'string') return { Id: m, DisplayName: m };
            const id = m.Id ?? m.id ?? m.$ref;
            const display = m.DisplayName ?? m.displayName ?? id;
            const entityType = m.EntityType ?? m.entityType;
            return id != null
                ? { Id: id, ...(display != null && { DisplayName: display }), ...(entityType != null && entityType !== '' && { EntityType: String(entityType).trim() }) }
                : null;
          }).filter(Boolean)
        : [];
    return {
        DisplayName: entity.DisplayName ?? entity.displayName ?? '',
        Description: entity.Description ?? entity.description ?? '',
        FilterScope: filterScopeStr,
        Type: entity.Type ?? entity.type ?? 'Manual',
        IsSystem: entity.IsSystem ?? entity.isSystem ?? false,
        ManualSetMember: manualArr,
        Filter: entity.Filter ?? entity.filter ?? ''
    };
}

function pathForPatchOp(name: string): string {
    return (name ?? '').toString().trim().replace(/^\$\./, '') || (name ?? '');
}

/**
 * True for the membership SCIM filter *clause* attribute (plain string), not reference fields
 * like FilterScope (IsReference → SchemaEntity) whose descriptions may mention "filter".
 */
function describesEnumeratedOnlyScimFilterClause(attr: any): boolean {
    if (attr?.isReference === true) return false;
    const d = (attr?.description ?? attr?.Description ?? '').toString().toLowerCase();
    return d.includes('scim') && d.includes('filter');
}

/**
 * Build current-data shape for Set PATCH diff (same shape as entity; used by generatePatchOperationsWithArrays).
 * When `schemaAttributes` is provided (typical edit with target entitlements), copy every form field that
 * appears in tier3 schema and has a value in formData — so dynamic attributes (e.g. Error, Notes) are not dropped.
 * When omitted, keep the legacy synthetic-Set shape for create/fallback without tier3.
 */
export function setFormDataToCurrentData(
    formData: Record<string, any>,
    originalData: Record<string, any>,
    schemaAttributes?: any[]
): Record<string, any> {
    const typeVal = formData.Type ?? originalData?.Type ?? 'Manual';
    const typeStr = typeof typeVal === 'string' ? typeVal : 'Manual';

    if (!schemaAttributes?.length) {
        const data: Record<string, any> = {
            ...originalData,
            DisplayName: formData.DisplayName ?? '',
            Description: formData.Description ?? '',
            FilterScope: formData.FilterScope || undefined,
            Type: typeStr,
            IsSystem: formData.IsSystem ?? false
        };
        if (typeStr.toLowerCase() === 'manual') {
            data.ManualSetMember = Array.isArray(formData.ManualSetMember) ? formData.ManualSetMember : [];
        } else {
            delete data.ManualSetMember;
        }
        if (typeStr.toLowerCase() === 'enumerated') {
            data.Filter = formData.Filter ?? '';
        } else {
            delete data.Filter;
        }
        return data;
    }

    const formRec = formData as Record<string, unknown>;
    const data: Record<string, any> = { ...originalData };

    for (const attr of schemaAttributes) {
        const name = getAttributeName(attr);
        if (!name) continue;
        const ref = createEntityRef(name);
        if (!entityHasEntityRef(formRec, ref)) continue;
        const plain = pathForPatchOp(name);
        data[plain] = getEntityValueFromARef(name, formRec);
    }

    const typeLower = typeStr.toLowerCase();
    if (typeLower !== 'manual') {
        for (const attr of schemaAttributes) {
            if (attr?.multiValued && attr?.isReference) {
                const plain = pathForPatchOp(getAttributeName(attr));
                if (plain) delete data[plain];
            }
        }
    }
    if (typeLower !== 'enumerated') {
        for (const attr of schemaAttributes) {
            if (describesEnumeratedOnlyScimFilterClause(attr)) {
                const plain = pathForPatchOp(getAttributeName(attr));
                if (plain) delete data[plain];
            }
        }
    }

    return data;
}
